import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { API } from "@/lib/api";
import { euro, t } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription,
} from "@/components/ui/sheet";

// Uses plain fetch (no auth) — public endpoints.
async function getJSON(path) {
  const r = await fetch(`${API}${path}`);
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function postJSON(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export default function CustomerMenu() {
  const { restaurantId, tableId } = useParams();
  const [rest, setRest] = useState(null);
  const [menu, setMenu] = useState({ categories: [], items: [] });
  const [table, setTable] = useState(null);
  const [cart, setCart] = useState({}); // { itemId: qty }
  const [customerName, setCustomerName] = useState("");
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [openSheet, setOpenSheet] = useState(false);
  const [myOrders, setMyOrders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, m, tb] = await Promise.all([
          getJSON(`/public/restaurants/${restaurantId}`),
          getJSON(`/public/restaurants/${restaurantId}/menu`),
          getJSON(`/public/tables/${tableId}`),
        ]);
        if (cancelled) return;
        setRest(r);
        setMenu(m || { categories: [], items: [] });
        setTable(tb);
        setActiveCat(m?.categories?.[0]?.id || null);
      } catch (e) { if (!cancelled) toast.error("Ristorante non disponibile"); }
    })();

    const doLoadOrders = async () => {
      try {
        const o = await getJSON(`/public/orders/table/${tableId}`);
        if (!cancelled) setMyOrders(Array.isArray(o) ? o : []);
      } catch {}
    };
    doLoadOrders();
    const iv = setInterval(doLoadOrders, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [restaurantId, tableId]);

  const addToCart = (id) => setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const removeFromCart = (id) => setCart((c) => {
    const n = { ...c }; if (!n[id]) return c;
    n[id] -= 1; if (n[id] <= 0) delete n[id]; return n;
  });

  const cartItems = useMemo(() => {
    const items = menu?.items || [];
    return Object.entries(cart).map(([id, qty]) => {
      const it = items.find((m) => m.id === id);
      return it ? { ...it, quantity: qty } : null;
    }).filter(Boolean);
  }, [cart, menu?.items]);

  const cartTotal = cartItems.reduce((s, it) => s + it.price * it.quantity, 0);
  const cartCount = cartItems.reduce((s, it) => s + it.quantity, 0);
  const currency = rest?.currency || "EUR";

  const placeOrder = async () => {
    if (cartItems.length === 0) return;
    setPlacing(true);
    try {
      await postJSON("/public/orders", {
        table_id: tableId,
        customer_name: customerName,
        notes: notes,
        items: cartItems.map((c) => ({ menu_item_id: c.id, quantity: c.quantity })),
      });
      toast.success(t.customer.order_sent);
      // Close the sheet first so Radix can run its cleanup effects,
      // THEN clear the cart on the next tick (prevents the Sheet from
      // being unmounted mid-animation which leaves pointer-events:none stuck on <body>).
      setOpenSheet(false);
      setTimeout(() => {
        setCart({});
        setNotes("");
      }, 320);
    } catch (e) { toast.error("Invio non riuscito"); }
    finally { setPlacing(false); }
  };

  const brandBg = rest?.background_color || "#FFFFFF";
  const brandPrimary = rest?.primary_color || "#1A1A1A";
  const brandAccent = rest?.accent_color || "#D4AF37";

  if (!rest) return <div className="min-h-screen flex items-center justify-center font-mobile">Caricamento…</div>;

  return (
    <div className="min-h-screen font-mobile" style={{ background: brandBg, color: brandPrimary }} data-testid="customer-menu">
      <div className="max-w-md mx-auto pb-40">
        {/* Header */}
        <header className="px-5 pt-8 pb-6">
          <div className="flex items-center gap-4">
            {rest.logo_url && <img src={rest.logo_url} alt="" className="h-14 w-14 rounded-full object-cover" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-60">{t.customer.table} {table?.label}</div>
              <h1 className="font-display text-3xl font-black tracking-tighter">{rest.name}</h1>
            </div>
          </div>
          {rest.description && <p className="mt-4 text-sm opacity-70">{rest.description}</p>}
        </header>

        {/* Category tabs */}
        <div className="sticky top-0 z-20 glass border-b border-black/5 px-3 py-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {menu.categories.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setActiveCat(c.id);
                  document.getElementById(`cat-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                data-testid={`cust-cat-${c.id}`}
                className="pill-btn px-4 py-1.5 text-sm whitespace-nowrap"
                style={{
                  background: activeCat === c.id ? brandAccent : "transparent",
                  color: activeCat === c.id ? "#fff" : brandPrimary,
                  border: activeCat === c.id ? "none" : `1px solid ${brandPrimary}22`,
                }}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Menu */}
        <div className="px-5 py-6 space-y-8">
          {menu.categories.map((c) => (
            <section key={c.id} id={`cat-${c.id}`}>
              <h2 className="font-display text-2xl font-bold tracking-tight mb-4">{c.name}</h2>
              <div className="space-y-4">
                {menu.items.filter((i) => i.category_id === c.id).map((it) => (
                  <div key={it.id} className="flex gap-4" data-testid={`cust-item-${it.id}`}>
                    <div className="flex-1">
                      <div className="font-semibold">{it.name}</div>
                      <div className="text-sm opacity-70 mt-1 line-clamp-2">{it.description}</div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="font-display font-bold text-lg">{euro(it.price, currency)}</div>
                        {cart[it.id] ? (
                          <div className="flex items-center gap-2 rounded-full border border-black/10 px-1">
                            <button onClick={() => removeFromCart(it.id)} data-testid={`cust-minus-${it.id}`} className="p-1"><Minus className="h-4 w-4" /></button>
                            <span className="font-medium w-6 text-center">{cart[it.id]}</span>
                            <button onClick={() => addToCart(it.id)} data-testid={`cust-plus-${it.id}`} className="p-1"><Plus className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addToCart(it.id)}
                            data-testid={`cust-add-${it.id}`}
                            className="pill-btn px-4 py-1.5 text-sm font-medium"
                            style={{ background: brandAccent, color: "#fff" }}
                          >
                            + {t.customer.add}
                          </button>
                        )}
                      </div>
                    </div>
                    {it.image_url && <img src={it.image_url} alt="" className="w-24 h-24 rounded-xl object-cover" />}
                  </div>
                ))}
              </div>
            </section>
          ))}
          {menu.items.length === 0 && (
            <div className="text-center opacity-60 py-16">Menu in preparazione…</div>
          )}
        </div>

        {/* My orders */}
        {myOrders.length > 0 && (
          <div className="px-5 pb-6">
            <h3 className="font-display text-lg font-bold mb-3">{t.customer.my_orders}</h3>
            <div className="space-y-2">
              {myOrders.map((o) => (
                <div key={o.id} className="p-3 rounded-xl border border-black/10 flex justify-between text-sm" data-testid={`cust-order-${o.id}`}>
                  <div>
                    <div className="font-medium">{o.items.length} articoli</div>
                    <div className="opacity-60 text-xs">{new Date(o.created_at).toLocaleTimeString("it-IT")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{euro(o.total, o.currency)}</div>
                    <div className="text-xs opacity-70">{t.order_status[o.status]}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky cart — Sheet stays ALWAYS MOUNTED to avoid Radix pointer-events leak. */}
      <Sheet
        open={openSheet && cartCount > 0}
        onOpenChange={(v) => setOpenSheet(v && cartCount > 0)}
      >
        <div
          className="fixed bottom-4 left-0 right-0 z-30 px-5 transition-opacity"
          style={{
            opacity: cartCount > 0 ? 1 : 0,
            pointerEvents: cartCount > 0 ? "auto" : "none",
          }}
          aria-hidden={cartCount === 0}
        >
          <div className="max-w-md mx-auto">
            <SheetTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between px-5 py-4 rounded-full shadow-xl"
                style={{ background: brandPrimary, color: brandBg }}
                data-testid="cust-cart-btn"
                disabled={cartCount === 0}
              >
                <span className="flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5" /> {cartCount} {cartCount === 1 ? "articolo" : "articoli"}
                </span>
                <span className="font-bold">{euro(cartTotal, currency)}</span>
              </button>
            </SheetTrigger>
          </div>
        </div>
        <SheetContent side="bottom" className="max-w-md mx-auto rounded-t-3xl" data-testid="cust-cart-sheet">
          <SheetHeader>
            <SheetTitle>Il tuo ordine</SheetTitle>
            <SheetDescription className="sr-only">Rivedi il tuo ordine prima di inviarlo.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 my-4">
            {cartItems.map((it) => (
              <div key={it.id} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium">{it.name}</div>
                  <div className="text-xs opacity-60">{euro(it.price, currency)}</div>
                </div>
                <div className="flex items-center gap-2 rounded-full border border-black/10 px-1">
                  <button type="button" onClick={() => removeFromCart(it.id)} className="p-1"><Minus className="h-4 w-4" /></button>
                  <span className="w-6 text-center">{it.quantity}</span>
                  <button type="button" onClick={() => addToCart(it.id)} className="p-1"><Plus className="h-4 w-4" /></button>
                </div>
                <div className="w-16 text-right font-bold">{euro(it.price * it.quantity, currency)}</div>
              </div>
            ))}
          </div>
          <Input placeholder={t.customer.optional_name} value={customerName} onChange={(e) => setCustomerName(e.target.value)} data-testid="cust-name-input" />
          <Textarea placeholder={t.customer.notes} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-2" data-testid="cust-notes-input" />
          <SheetFooter className="mt-4">
            <div className="w-full space-y-3">
              <div className="flex justify-between font-display text-xl font-bold">
                <span>{t.customer.total}</span><span>{euro(cartTotal, currency)}</span>
              </div>
              <Button
                type="button"
                onClick={placeOrder}
                disabled={placing || cartItems.length === 0}
                className="w-full pill-btn h-12"
                style={{ background: brandAccent, color: "#fff" }}
                data-testid="cust-place-order-btn"
              >
                {placing ? "…" : t.customer.place_order}
              </Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
